import { useTranslation } from "react-i18next";

import type { UnmappedLabel } from "./types";

export type ChartEmptyKind =
  | "loading"
  | "no-price"
  | "no-l6"
  | "l6-cold-start"
  | "no-finbert"
  | "disabled"
  | "degraded"
  | "error"
  | "unmapped-labels";

export interface ChartEmptyStateProps {
  kind: ChartEmptyKind;
  l6SeriesStartsAt?: string;
  unmappedLabels?: UnmappedLabel[];
  /** Required for kind="error" — re-runs the failed queries. */
  onRetry?: () => void;
}

const SKELETON_HEIGHT = 380;

export function ChartEmptyState({
  kind,
  l6SeriesStartsAt,
  unmappedLabels,
  onRetry,
}: ChartEmptyStateProps) {
  const { t } = useTranslation("dashboard");
  if (kind === "loading") {
    return (
      <div
        data-testid="chart-empty"
        data-state="loading"
        className="w-full bg-muted/30 animate-pulse rounded"
        style={{ height: SKELETON_HEIGHT }}
      />
    );
  }
  if (kind === "error") {
    // An honest failure state. Previously a network error or 500 fell
    // through to "Waiting for MT5 EA" / "No reasoned analyses yet" —
    // affirmatively wrong claims while the API was down.
    return (
      <div
        data-testid="chart-empty"
        data-state="error"
        className="text-sm py-2 flex items-center gap-2 text-destructive"
      >
        <span>{t("chart.loadError")}</span>
        <button
          type="button"
          onClick={onRetry}
          className="px-2 py-0.5 text-xs rounded border border-current"
        >
          {t("chart.retry")}
        </button>
      </div>
    );
  }
  if (kind === "no-price") {
    return (
      <div data-testid="chart-empty" data-state="no-price" className="text-sm py-2">
        {t("chart.noPrice")}
      </div>
    );
  }
  if (kind === "no-l6") {
    return (
      <div data-testid="chart-empty" data-state="no-l6" className="text-sm py-2">
        {t("chart.noL6")}
      </div>
    );
  }
  if (kind === "l6-cold-start") {
    const display = l6SeriesStartsAt
      ? new Date(l6SeriesStartsAt).toISOString().slice(0, 10)
      : "—";
    return (
      <div data-testid="chart-empty" data-state="l6-cold-start" className="text-xs py-1">
        {t("chart.coldStart", { date: display })}
      </div>
    );
  }
  if (kind === "no-finbert") {
    return (
      <div data-testid="chart-empty" data-state="no-finbert" className="text-xs py-1">
        {t("chart.noFinbert")}
      </div>
    );
  }
  if (kind === "disabled") {
    return (
      <div data-testid="chart-empty" data-state="disabled" className="text-sm py-2">
        {t("chart.disabled")}
      </div>
    );
  }
  if (kind === "degraded") {
    return (
      <div data-testid="chart-empty" data-state="degraded" className="text-sm py-2">
        {t("chart.degraded")}
      </div>
    );
  }
  // unmapped-labels toast
  const n = unmappedLabels?.length ?? 0;
  return (
    <div
      data-testid="chart-empty"
      data-state="unmapped-labels"
      className="text-xs py-1 italic"
    >
      {t("chart.unmappedLabels", { count: n })}
    </div>
  );
}
